import React, { useState, useEffect, useRef } from "react";
import { useActivity } from "../../hooks/useActivity";
import { usePreferences } from "../../hooks/usePreferences";
import {
  formatSpeed,
  formatSpeedParts,
  formatDistance,
  formatDuration,
} from "../../utils/formatting";
import { ConfirmationDialog } from "../Activities/ConfirmationDialog";

export function ActivityBar() {
  const {
    state,
    metrics,
    startActivity,
    stopActivity,
    pauseActivity,
    resumeActivity,
    getElapsedTime,
  } = useActivity();
  const { userPreferences } = usePreferences();
  const units = userPreferences.units || "metric";
  const layout = userPreferences.activityBarLayout || "expanded";
  const [displayDuration, setDisplayDuration] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const [showStopConfirmation, setShowStopConfirmation] = useState(false);
  const [showPauseConfirmation, setShowPauseConfirmation] = useState(false);

  // Use requestAnimationFrame for smooth timer updates without re-rendering parent
  useEffect(() => {
    if (state === "recording" && getElapsedTime) {
      const updateTimer = () => {
        setDisplayDuration(getElapsedTime());
        animationFrameRef.current = requestAnimationFrame(updateTimer);
      };
      animationFrameRef.current = requestAnimationFrame(updateTimer);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else if (state === "paused" && getElapsedTime) {
      // When paused, show the current elapsed time (frozen)
      setDisplayDuration(getElapsedTime());
      return undefined;
    } else {
      // When idle, just use the stored duration
      setDisplayDuration(metrics.duration);
      return undefined;
    }
  }, [state, metrics.duration, getElapsedTime]);

  const handlePrimaryAction = () => {
    const confirmationMode = userPreferences.confirmationMode ?? "stop";

    switch (state) {
      case "idle":
        startActivity();
        break;
      case "recording":
        // Show confirmation if mode is "both"
        if (confirmationMode === "both") {
          setShowPauseConfirmation(true);
        } else {
          pauseActivity();
        }
        break;
      case "paused":
        resumeActivity();
        break;
    }
  };

  const getPrimaryActionLabel = () => {
    switch (state) {
      case "idle":
        return "Start";
      case "recording":
        return "Pause";
      case "paused":
        return "Resume";
      default:
        return "Start";
    }
  };

  const handleStop = () => {
    const confirmationMode = userPreferences.confirmationMode ?? "stop";
    const duration = getElapsedTime();
    const distance = metrics.distance;

    // Check if activity is long enough to save
    // Don't save if: duration < 30s OR (duration < 5min AND distance < 0.25mi)
    const isTooShort =
      duration < 30000 || // < 30 seconds
      (duration < 300000 && distance < 402.335); // < 5 minutes AND < 0.25 miles (402.335 meters)

    // Show confirmation if mode is "stop" or "both", and activity is long enough
    const shouldConfirm =
      !isTooShort &&
      state !== "idle" &&
      (confirmationMode === "stop" || confirmationMode === "both");

    if (shouldConfirm) {
      setShowStopConfirmation(true);
    } else {
      stopActivity();
    }
  };

  const confirmStop = () => {
    setShowStopConfirmation(false);
    stopActivity();
  };

  const confirmPause = () => {
    setShowPauseConfirmation(false);
    pauseActivity();
  };

  // Render expanded layout (default)
  if (layout === "expanded") {
    return (
      <>
        <div className="activity-bar-container">
          {state !== "idle" && (
            <div className="activity-controls-floating">
              <button
                className={`control-btn-floating primary ${state}`}
                onClick={handlePrimaryAction}
              >
                {getPrimaryActionLabel()}
              </button>
              <button
                className="control-btn-floating stop"
                onClick={handleStop}
              >
                Stop
              </button>
            </div>
          )}
          <div
            className={`activity-bar activity-bar--expanded ${state !== "idle" ? "active" : ""}`}
          >
            <div className="metrics">
              <div className="metric metric--secondary">
                <span className="metric-value">
                  {formatDistance(metrics.distance, units, true)}
                </span>
                <span className="metric-label">
                  {units === "metric" ? "km" : "mi"}
                </span>
              </div>
              <div className="metric metric--primary">
                <span className="metric-value">
                  {(() => {
                    const { integer, decimal, value } = formatSpeedParts(
                      metrics.currentSpeed,
                      units,
                    );
                    const decimalClass =
                      value >= 10
                        ? "speed-decimal speed-decimal--small"
                        : "speed-decimal";
                    return (
                      <>
                        {integer}
                        <span className={decimalClass}>.{decimal}</span>
                      </>
                    );
                  })()}
                </span>
                <span className="metric-label">
                  {units === "metric" ? "km/h" : "mph"}
                </span>
              </div>
              {state === "idle" ? (
                <button className="control-btn-start" onClick={startActivity}>
                  Start
                </button>
              ) : (
                <div className="metric metric--secondary">
                  <span className="metric-value">
                    {formatDuration(displayDuration)}
                  </span>
                  <span className="metric-label">time</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showStopConfirmation}
          title="Stop Activity?"
          message="Are you sure you want to stop and save this activity?"
          confirmText="Stop & Save"
          cancelText="Continue"
          onConfirm={confirmStop}
          onCancel={() => setShowStopConfirmation(false)}
        />

        <ConfirmationDialog
          visible={showPauseConfirmation}
          title="Pause Activity?"
          message="Are you sure you want to pause this activity?"
          confirmText="Pause"
          cancelText="Continue"
          onConfirm={confirmPause}
          onCancel={() => setShowPauseConfirmation(false)}
        />
      </>
    );
  }

  // Render compact layout
  if (layout === "compact") {
    return (
      <>
        <div className="activity-bar-container">
          {state !== "idle" && (
            <div className="activity-controls-floating">
              <button
                className={`control-btn-floating primary ${state}`}
                onClick={handlePrimaryAction}
              >
                {getPrimaryActionLabel()}
              </button>
              <button
                className="control-btn-floating stop"
                onClick={handleStop}
              >
                Stop
              </button>
            </div>
          )}
          <div
            className={`activity-bar activity-bar--compact ${state !== "idle" ? "active" : ""}`}
          >
            <div className="metrics">
              <div className="metric metric--inline">
                <span className="metric-value">
                  {formatDistance(metrics.distance, units, true)}
                </span>
                <span className="metric-label">
                  {units === "metric" ? "km" : "mi"}
                </span>
              </div>
              <div className="metric metric--inline">
                <span className="metric-value">
                  {(() => {
                    const { integer, decimal, value } = formatSpeedParts(
                      metrics.currentSpeed,
                      units,
                    );
                    const decimalClass =
                      value >= 10
                        ? "speed-decimal speed-decimal--small"
                        : "speed-decimal";
                    return (
                      <>
                        {integer}
                        <span className={decimalClass}>.{decimal}</span>
                      </>
                    );
                  })()}
                </span>
                <span className="metric-label">
                  {units === "metric" ? "km/h" : "mph"}
                </span>
              </div>
              {state === "idle" ? (
                <button
                  className="control-btn-start-compact"
                  onClick={startActivity}
                >
                  Start
                </button>
              ) : (
                <div className="metric metric--inline">
                  <span className="metric-label">time</span>
                  <span className="metric-value">
                    {formatDuration(displayDuration)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showStopConfirmation}
          title="Stop Activity?"
          message="Are you sure you want to stop and save this activity?"
          confirmText="Stop & Save"
          cancelText="Continue"
          onConfirm={confirmStop}
          onCancel={() => setShowStopConfirmation(false)}
        />

        <ConfirmationDialog
          visible={showPauseConfirmation}
          title="Pause Activity?"
          message="Are you sure you want to pause this activity?"
          confirmText="Pause"
          cancelText="Continue"
          onConfirm={confirmPause}
          onCancel={() => setShowPauseConfirmation(false)}
        />
      </>
    );
  }

  // Render overlay layout - optimized for visibility over map
  return (
    <>
      <div className="activity-bar-container">
        {state !== "idle" && (
          <div className="activity-controls-floating">
            <button
              className={`control-btn-floating primary ${state}`}
              onClick={handlePrimaryAction}
            >
              {getPrimaryActionLabel()}
            </button>
            <button className="control-btn-floating stop" onClick={handleStop}>
              Stop
            </button>
          </div>
        )}
        <div
          className={`activity-bar activity-bar--overlay ${state !== "idle" ? "active" : ""}`}
        >
          {/* Left side: Distance metric */}
          <div className="overlay-side overlay-side-left">
            <div className="metric-secondary">
              <span className="metric-value-secondary">
                {formatDistance(metrics.distance, units, true)}
              </span>
              <span className="metric-label-secondary">
                {units === "metric" ? "km" : "mi"}
              </span>
            </div>
          </div>

          {/* Center: MPH label with speed value absolutely positioned above */}
          <div className="overlay-center">
            <span className="metric-value-hero">
              {(() => {
                const { integer, decimal, value } = formatSpeedParts(
                  metrics.currentSpeed,
                  units,
                );
                const decimalClass =
                  value >= 10
                    ? "speed-decimal speed-decimal--small"
                    : "speed-decimal";
                return (
                  <>
                    {integer}
                    <span className={decimalClass}>
                      <span className="speed-decimal-point">.</span>
                      {decimal}
                    </span>
                  </>
                );
              })()}
            </span>
            <span className="metric-label-hero">
              {units === "metric" ? "km/h" : "mph"}
            </span>
          </div>

          {/* Right side: Time metric or Start button */}
          <div className="overlay-side overlay-side-right">
            {state === "idle" ? (
              <button
                className="control-btn-start-overlay"
                onClick={startActivity}
              >
                Start
              </button>
            ) : (
              <div className="metric-secondary">
                <span className="metric-label-secondary">time</span>
                <span className="metric-value-secondary">
                  {formatDuration(displayDuration)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        visible={showStopConfirmation}
        title="Stop Activity?"
        message="Are you sure you want to stop and save this activity?"
        confirmText="Stop & Save"
        cancelText="Continue"
        onConfirm={confirmStop}
        onCancel={() => setShowStopConfirmation(false)}
      />

      <ConfirmationDialog
        visible={showPauseConfirmation}
        title="Pause Activity?"
        message="Are you sure you want to pause this activity?"
        confirmText="Pause"
        cancelText="Continue"
        onConfirm={confirmPause}
        onCancel={() => setShowPauseConfirmation(false)}
      />
    </>
  );
}
